import { type MediaRef, mediaSupport } from "@rhema/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useClient } from "../session/session";
import { type Playable, playableUrl } from "./playback";

type State = { url: string | null; mode: Playable["mode"] | null; loading: boolean; error: string | null };

const IDLE: State = { url: null, mode: null, loading: false, error: null };

/** Opens the video from a local copy, where this device still has the file it is sending. */
function fromLocal(file: Blob): Playable {
  const url = URL.createObjectURL(file);
  return { url, mode: "blob", release: () => URL.revokeObjectURL(url) };
}

/**
 * A URL a <video> can play, asked for only when `start` is called. Large files stream while they
 * download. The stream or object URL is let go when the player closes, the file changes, or the
 * component goes away.
 */
export function usePlayable(ref: MediaRef | undefined, local?: Blob) {
  const client = useClient();
  const [state, setState] = useState<State>(IDLE);
  const held = useRef<Playable | null>(null);
  const live = useRef(true);
  const latest = useRef(ref);
  latest.current = ref;
  const key = ref?.key;

  const release = useCallback(() => {
    held.current?.release();
    held.current = null;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the storage key says whether it is a new file
  const start = useCallback(async (): Promise<string | null> => {
    if (held.current) return held.current.url;
    const current = latest.current;
    if (!local && !current?.key) return null;
    if (current && !local && mediaSupport(current) === "update") {
      setState({ ...IDLE, error: "Update Rhema to open this file." });
      return null;
    }
    setState({ ...IDLE, loading: true });
    try {
      const playable = local
        ? fromLocal(local)
        : await playableUrl(current as MediaRef, {
            mediaUrl: async (k) => (await client.api.mediaUrl(k, { chunked: true })).url,
            download: (r) => client.media.download(r),
          });
      if (!live.current || latest.current?.key !== current?.key) {
        playable.release();
        return null;
      }
      held.current = playable;
      setState({ url: playable.url, mode: playable.mode, loading: false, error: null });
      return playable.url;
    } catch (err) {
      if (live.current) setState({ ...IDLE, error: (err as Error).message || "Could not open" });
      return null;
    }
  }, [client, key, local]);

  const stop = useCallback(() => {
    release();
    if (live.current) setState(IDLE);
  }, [release]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new file lets go of the old one
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      release();
      setState(IDLE);
    };
  }, [key, local, release]);

  return { ...state, start, stop };
}
